import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProviderPhoto } from "@/components/ProviderPhoto";
import { applyDefaultAvatars, setProviderAvatarGender } from "@/server/provider-avatars.functions";
import { Loader2, Upload, ShieldAlert, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import {
 loadAvatarAssetsFn,
 loadReviewProvidersFn,
 loadPendingCountFn,
 updateAvatarAssetUrlFn,
} from "./-dashboard.admin.avatars.functions";

export const Route = createFileRoute("/dashboard/admin/avatars")({
 component: AdminAvatarsPage,
});

type AssetRow = { asset_key: string; asset_name: string; asset_url: string };
type ProvRow = {
 id: string;
 name: string;
 profile_photo_url: string | null;
 display_photo_url: string | null;
 avatar_gender: string | null;
 avatar_source: string | null;
 avatar_confidence: number | null;
 has_custom_photo: boolean;
 avatar_needs_review: boolean;
};

function AdminAvatarsPage() {
 const { user, loading, primaryRole } = useAuth();
 const navigate = useNavigate();
 const apply = useServerFn(applyDefaultAvatars);
 const setGender = useServerFn(setProviderAvatarGender);

 const [assets, setAssets] = useState<AssetRow[]>([]);
 const [reviewProviders, setReviewProviders] = useState<ProvRow[]>([]);
 const [busy, setBusy] = useState(false);
 const [uploading, setUploading] = useState<string | null>(null);
 const [pending, setPending] = useState<number>(0);
 const [progress, setProgress] = useState<{ done: number; remaining: number } | null>(null);
 const [continuous, setContinuous] = useState(false);
 const stopRef = useRef(false);

 const reload = async () => {
 const [a, ps, pendingCount] = await Promise.all([
 loadAvatarAssetsFn(),
 loadReviewProvidersFn(),
 loadPendingCountFn(),
 ]);
 setAssets((a ?? []) as AssetRow[]);
 setReviewProviders((ps ?? []) as ProvRow[]);
 setPending(pendingCount?.count ?? 0);
 };

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 if (primaryRole && primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
 reload();
 }, [user, loading, primaryRole]);

 if (loading || !user || primaryRole !== "admin") {
 return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
 }

 const female = assets.find((a) => a.asset_key === "default_provider_avatar_female");
 const male = assets.find((a) => a.asset_key === "default_provider_avatar_male");

 const uploadAsset = async (key: "default_provider_avatar_female" | "default_provider_avatar_male", file: File) => {
 setUploading(key);
 try {
 const ext = (file.name.split(".").pop() || "png").toLowerCase();
 const path = `defaults/${key}-${Date.now()}.${ext}`;
 const { getAppAssetUploadUrl } = await import("@/server/storage.functions");
 const { uploadUrl, publicUrl } = await getAppAssetUploadUrl({
 data: { key: path, contentType: file.type },
 });
 const putRes = await fetch(uploadUrl, {
 method: "PUT",
 headers: { "Content-Type": file.type },
 body: file,
 });
 if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
 await updateAvatarAssetUrlFn({ data: { key, assetUrl: publicUrl } });
 toast.success("Default avatar updated");
 await reload();
 } catch (e: any) {
 toast.error(e?.message ?? "Upload failed");
 } finally {
 setUploading(null);
 }
 };

 const handleApplyOnce = async () => {
 setBusy(true);
 stopRef.current = false;
 setProgress({ done: 0, remaining: pending });
 try {
 if (!continuous) {
 const res = await apply({ data: { batchSize: 5000 } });
 setProgress({ done: res.updated, remaining: res.remaining });
 toast.success(`Applied ${res.updated.toLocaleString()} providers · ${res.remaining.toLocaleString()} remaining`);
 } else {
 let totalUpdated = 0;
 let totalReview = 0;
 // Safety cap: 2000 batches × 5000 = up to 10M rows.
 for (let i = 0; i < 2000; i++) {
 if (stopRef.current) break;
 const res = await apply({ data: { batchSize: 5000 } });
 totalUpdated += res.updated;
 totalReview += res.needsReview;
 setProgress({ done: totalUpdated, remaining: res.remaining });
 if (res.remaining === 0 || res.updated === 0) break;
 }
 toast.success(`Done. Updated ${totalUpdated.toLocaleString()} providers (${totalReview.toLocaleString()} need review).`);
 }
 await reload();
 } catch (e: any) {
 toast.error(e?.message ?? "Apply failed");
 } finally {
 setBusy(false);
 stopRef.current = false;
 }
 };

 const handleStop = () => {
 stopRef.current = true;
 };

 const handleSetGender = async (id: string, gender: "male" | "female" | "unknown") => {
 try {
 await setGender({ data: { providerId: id, gender } });
 toast.success("Updated");
 await reload();
 } catch (e: any) {
 toast.error(e?.message ?? "Failed");
 }
 };

 return (
 <AdminPageShell
   title="Avatars"
   description="Default provider silhouettes and avatar review queue."
   primaryAction={{ label: "Refresh", icon: RefreshCw, onClick: reload }}
 >
 <div className="space-y-12">

 <section className="grid gap-6 md:grid-cols-2">
 <DefaultAvatarCard
 title="Female silhouette"
 asset={female}
 disabled={uploading !== null}
 isUploading={uploading === "default_provider_avatar_female"}
 onUpload={(f) => uploadAsset("default_provider_avatar_female", f)}
 />
 <DefaultAvatarCard
 title="Male silhouette"
 asset={male}
 disabled={uploading !== null}
 isUploading={uploading === "default_provider_avatar_male"}
 onUpload={(f) => uploadAsset("default_provider_avatar_male", f)}
 />
 </section>

 <section className="card-surface rounded-md border border-border bg-card p-6">
 <div className="flex items-start justify-between gap-4 flex-wrap">
 <div>
 <h2 className="font-display text-lg font-semibold">Apply Default Avatars</h2>
 <p className="text-sm text-muted-foreground mt-1 max-w-xl">
 Sweep providers without a custom photo and assign the matching silhouette (5,000 per batch). Custom photos are never overwritten. Enable "Run continuously" to keep going until the queue is empty.
 </p>
 <p className="text-sm mt-3">
 <span className="font-medium">{pending.toLocaleString()}</span>{" "}
 <span className="text-muted-foreground">providers still need an avatar assigned.</span>
 </p>
 {progress ? (
 <p className="text-xs text-muted-foreground mt-1">
 Processed {progress.done.toLocaleString()} this run · {progress.remaining.toLocaleString()} remaining
 </p>
 ) : null}
 <label className="flex items-center gap-2 mt-3 text-sm">
 <input
 type="checkbox"
 checked={continuous}
 onChange={(e) => setContinuous(e.target.checked)}
 disabled={busy}
 />
 Run continuously until queue is empty
 </label>
 </div>
 <div className="flex gap-2">
 {busy && continuous ? (
 <Button variant="outline" onClick={handleStop}>Stop</Button>
 ) : null}
 <Button onClick={handleApplyOnce} disabled={busy || pending === 0}>
 {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
 {busy ? "Applying…" : continuous ? "Apply All Avatars" : "Apply Next 5,000"}
 </Button>
 </div>
 </div>
 </section>



 <section>
 <div className="flex items-center gap-2 mb-4">
 <ShieldAlert className="h-4 w-4 text-amber-600" />
 <h2 className="font-display text-lg font-semibold">Providers needing avatar review ({reviewProviders.length})</h2>
 </div>
 {reviewProviders.length === 0 ? (
 <p className="text-sm text-muted-foreground">All providers have a confident default avatar.</p>
 ) : (
 <div className="card-surface rounded-md border border-border bg-card divide-y divide-border">
 {reviewProviders.map((p) => (
 <div key={p.id} className="card-surface-flat rounded-md p-4 flex items-center gap-4">
 <ProviderPhoto provider={p as any} className="h-12 w-12 border border-border" />
 <div className="flex-1 min-w-0">
 <p className="font-medium truncate">{p.name}</p>
 <p className="text-xs text-muted-foreground">
 {p.avatar_source ?? " "} · confidence {p.avatar_confidence ?? 0}%
 </p>
 </div>
 <div className="flex gap-2">
 <Button size="sm" variant="outline" onClick={() => handleSetGender(p.id, "female")}>Female</Button>
 <Button size="sm" variant="outline" onClick={() => handleSetGender(p.id, "male")}>Male</Button>
 <Button size="sm" variant="ghost" onClick={() => handleSetGender(p.id, "unknown")}>Unknown</Button>
 </div>
 </div>
 ))}
 </div>
 )}
 </section>
 </div>
 </AdminPageShell>
 );
}

function DefaultAvatarCard({
 title, asset, disabled, isUploading, onUpload,
}: { title: string; asset?: AssetRow; disabled: boolean; isUploading: boolean; onUpload: (f: File) => void }) {
 return (
 <div className="card-surface-flat rounded-md border border-border bg-card p-6 flex flex-col items-center text-center gap-4">
 <h3 className="font-display font-semibold">{title}</h3>
 {asset?.asset_url ? (
 <img src={asset.asset_url} alt={asset.asset_name} className="h-40 w-40 rounded-md object-cover border border-border bg-muted" loading="lazy" decoding="async" />
 ) : (
 <div className="h-40 w-40 rounded-md bg-muted border border-border" />
 )}
 <Label className="w-full cursor-pointer">
 <div className={`flex items-center justify-center gap-2 rounded-md border border-border px-4 py-2 text-sm hover:bg-accent ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
 {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
 {isUploading ? "Uploading…" : "Replace image"}
 </div>
 <Input
 type="file"
 accept="image/png,image/jpeg,image/webp"
 className="hidden"
 disabled={disabled}
 onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}
 />
 </Label>
 </div>
 );
}
